
import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  onDayDoubleClick?: (day: Date) => void;
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  onDayDoubleClick,
  ...props
}: CalendarProps) {
  // Handle double click on calendar container
  const handleContainerDoubleClick = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!onDayDoubleClick) return;
    
    // Find the button element that was double-clicked
    const target = e.target as HTMLElement;
    const dayButton = target.closest('button[name="day"]');
    
    if (dayButton) {
      // Get the date from the button's data attribute or aria-label
      const dateStr = dayButton.getAttribute('aria-label');
      if (dateStr) {
        const parsedDate = new Date(dateStr);
        if (!isNaN(parsedDate.getTime())) {
          onDayDoubleClick(parsedDate);
        }
      }
    }
  }, [onDayDoubleClick]);

  const defaultClassNames = {
    months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
    month: "space-y-4 w-full",
    caption: "flex justify-center pt-1 relative items-center",
    caption_label: "text-base font-medium",
    nav: "space-x-1 flex items-center",
    nav_button: cn(
      buttonVariants({ variant: "outline" }),
      "h-8 w-8 bg-transparent p-0 opacity-50 hover:opacity-100"
    ),
    nav_button_previous: "absolute left-1",
    nav_button_next: "absolute right-1",
    table: "w-full border-collapse space-y-1",
    head_row: "flex",
    head_cell:
      "text-muted-foreground rounded-md w-14 font-medium text-[0.9rem]",
    row: "flex w-full mt-2",
    cell: "h-14 w-14 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
    day: cn(
      buttonVariants({ variant: "ghost" }),
      "h-14 w-14 p-0 font-normal aria-selected:opacity-100 transition-all"
    ),
    day_range_end: "day-range-end",
    day_selected:
      "bg-cs-primary text-white hover:bg-cs-primary hover:text-white focus:bg-cs-primary focus:text-white",
    day_today: "bg-cs-accent text-cs-primary font-medium ring-1 ring-cs-primary ring-offset-1",
    day_outside:
      "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
    day_disabled: "text-muted-foreground opacity-50",
    day_range_middle:
      "aria-selected:bg-accent aria-selected:text-accent-foreground",
    day_hidden: "invisible",
  };

  return (
    <div onDoubleClick={handleContainerDoubleClick}>
      <DayPicker
        showOutsideDays={showOutsideDays}
        className={cn("p-3 pointer-events-auto", className)}
        classNames={{
          ...classNames,
          months: cn(defaultClassNames.months, classNames?.months),
          month: cn(defaultClassNames.month, classNames?.month),
          caption: cn(defaultClassNames.caption, classNames?.caption),
          caption_label: cn(defaultClassNames.caption_label, classNames?.caption_label),
          nav: cn(defaultClassNames.nav, classNames?.nav),
          nav_button: cn(defaultClassNames.nav_button, classNames?.nav_button),
          nav_button_previous: cn(defaultClassNames.nav_button_previous, classNames?.nav_button_previous),
          nav_button_next: cn(defaultClassNames.nav_button_next, classNames?.nav_button_next),
          table: cn(defaultClassNames.table, classNames?.table),
          head_row: cn(defaultClassNames.head_row, classNames?.head_row),
          head_cell: cn(defaultClassNames.head_cell, classNames?.head_cell),
          row: cn(defaultClassNames.row, classNames?.row),
          cell: cn(defaultClassNames.cell, classNames?.cell),
          day: cn(defaultClassNames.day, classNames?.day),
          day_range_end: cn(defaultClassNames.day_range_end, classNames?.day_range_end),
          day_selected: cn(defaultClassNames.day_selected, classNames?.day_selected),
          day_today: cn(defaultClassNames.day_today, classNames?.day_today),
          day_outside: cn(defaultClassNames.day_outside, classNames?.day_outside),
          day_disabled: cn(defaultClassNames.day_disabled, classNames?.day_disabled),
          day_range_middle: cn(defaultClassNames.day_range_middle, classNames?.day_range_middle),
          day_hidden: cn(defaultClassNames.day_hidden, classNames?.day_hidden),
        }}
        components={{
          IconLeft: () => <ChevronLeft className="h-5 w-5" />,
          IconRight: () => <ChevronRight className="h-5 w-5" />,
        }}
        {...props}
      />
    </div>
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
